import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { noMatchSentence, noMatchTitle } from '@/lib/session-search';
import { Button, Icon } from '@pms-digital/design-system';

/**
 * « Rien trouvé » (`recherche-sessions.md` §6, plate `Etats`): the empty state of the
 * design system for a search that retains no session - a `gray-100` disc with
 * `search_off`, the title that quotes what was searched, the rule, and when other
 * filters are armed how many of them narrow what the search reads, and the primary
 * button that empties the search and KEEPS those filters (H4).
 *
 * ## The button is the app's primary, not the plate's black
 *
 * The plate draws it `--pms-noir`. The repository's charte guard refuses a black ground
 * (`design-system-adherence.test.ts`: « there is no dark ground ») and its `Button` is
 * bleu nuit for the primary action, so this is the package `Button` - 44 px, the same radius
 * and weight, another ink. The second deviation is the glyph: the plate draws 64 px in a
 * 128 px disc, and the charte stops icons at 32 px (« bloc éditorial »), so the package
 * `Icon` draws 32 px in a 64 px disc, the plate's proportion.
 *
 * A search with no result is the only case that shows it: without a search the list's
 * own empty sentence is unchanged.
 */

export interface SearchEmptyStateProps {
  /** The search as the address holds it. */
  readonly query: string;
  /** How many filters are armed besides the search. */
  readonly activeFilters: number;
  readonly onReset: () => void;
}

export function SearchEmptyState({ query, activeFilters, onReset }: SearchEmptyStateProps) {
  return (
    <div
      data-testid={SESSIONS_TEST_IDS.searchEmpty}
      className="flex flex-col items-center gap-4 border-gray-200 border-t px-4 pt-12 pb-6 text-center"
    >
      <div
        aria-hidden="true"
        className="flex size-16 items-center justify-center rounded-circle bg-gray-100 text-gray-500"
      >
        <Icon name="search_off" size={32} />
      </div>
      <h2 className="break-words font-bold text-body-lg text-noir">{noMatchTitle(query)}</h2>
      <p className="max-w-105 text-gray-700 text-small leading-normal">
        {noMatchSentence(activeFilters)}
      </p>
      <Button
        type="button"
        icon="restart_alt"
        data-testid={SESSIONS_TEST_IDS.searchReset}
        onClick={onReset}
      >
        Réinitialiser la recherche
      </Button>
    </div>
  );
}
